import { observeElementOffset } from '@tanstack/react-virtual';

/**
 * The virtualizer's offset observer, plus ONE thing the stock one does not do:
 * tell the virtualizer where the element already is, the moment it binds.
 *
 * TanStack's `_willUpdate` binds to a scroll element and then WRITES its own
 * offset into it (`_scrollToOffset(getScrollOffset())`). It never reads the element
 * first — its offset only changes on a scroll EVENT. So after binding to an element
 * that is not where the virtualizer believes it is, the element is thrown to that
 * belief, which starts at 0:
 *
 *  · fast scrolling while rows arrive in chunks — the grid re-bound to the real
 *    OverlayScrollbars viewport mid-scroll and jumped from row ~80 back to row 0
 *    (owner, 19.09.2026: "above 200, 300 rows on fast scroll down it kind of fails");
 *  · coming back to the DB Manager — the restore and this write fought over the
 *    element for a few frames ("it kind of jiggles") and 0 won.
 *
 * The subscriptions are made BEFORE that write, so reporting the real offset here
 * turns the write into "scroll to where you are". Nothing is raced and nothing is
 * suppressed — the virtualizer is simply no longer wrong.
 */
export const SETTLE_FRAMES = 30;

export function observeOffsetFromElement(instance, cb) {
  const el = instance.scrollElement;
  if (!el || typeof el.scrollTop !== 'number') return observeElementOffset(instance, cb);
  cb(el.scrollTop, false);

  // …and KEEP AGREEING for a moment. Around a bind the element is moved by
  // others — the scroll restore puts a returning tab back where it was, and
  // OverlayScrollbars finishes taking over — and a scroll event fired in that
  // window does not always reach the listener that is just being attached.
  // Measured: the element sat at 54,000 px while the virtualizer still believed
  // 0 and drew rows 0–35 — the right position over a blank grid — until the
  // next real scroll event. A number comparison per frame for half a second
  // after each bind makes the virtualizer converge on the element whatever the
  // order those things happened in; after that, scroll events are the only
  // source, as before.
  let frames = 0;
  let raf = 0;
  const converge = () => {
    if (el.scrollTop !== instance.scrollOffset) cb(el.scrollTop, false);
    frames += 1;
    raf = frames < SETTLE_FRAMES ? requestAnimationFrame(converge) : 0;
  };
  raf = requestAnimationFrame(converge);
  const stop = observeElementOffset(instance, cb);
  return () => {
    if (raf) cancelAnimationFrame(raf);
    stop();
  };
}

/**
 * The horizontal results grid: one row per record, columns across.
 *
 * Presentational. Every decision it makes is a prop — what is selected, what
 * is staged, which columns are hidden — so the tab owns the state and this
 * owns the table.
 */
/**
 * Pinned (measured, not dragged) column widths by tab + visible column set.
 * Module level: the grid is one component for every DB tab, and the record
 * view swaps it out, so a ref inside it forgot them. Bounded; a dragged width
 * is stored on the tab itself and wins over these.
 */
export const PINNED_WIDTHS = new Map();

export const __resetPinnedWidths = () => PINNED_WIDTHS.clear();
